import { useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { InstrumentTypeResponse } from "../../../codegen-api";
import { APIEndpointEnum } from "../../../enums/endpoint";
import { TradeHistoryItem } from "../../../interfaces/history";
import { AssetResponse } from "../../../utils/asset";
import {
  useCursorPaginatedHistory,
  UseCursorPaginatedHistoryResult,
} from "./useCursorPaginatedHistory";

interface UseTradeHistoryOptions {
  pageSize?: number;
  instrumentType?: InstrumentTypeResponse;
  /** Server-side filter — only history for this underlying asset. */
  asset?: AssetResponse;
  enabled?: boolean;
}

/**
 * `/trade-history?account=…` — fills only, no synthesized P&L. Position-level
 * P&L lives in `usePositionHistory`.
 */
export const useTradeHistory = ({
  pageSize,
  instrumentType,
  asset,
  enabled = true,
}: UseTradeHistoryOptions = {}): UseCursorPaginatedHistoryResult<TradeHistoryItem> => {
  const { account } = useContext(AuthContext);
  const accountKey = account?.toLowerCase();

  const realtimeChannels = useMemo(
    () => (accountKey ? [`trade-history:${accountKey}`] : []),
    [accountKey]
  );

  return useCursorPaginatedHistory<TradeHistoryItem>({
    endpoint: APIEndpointEnum.TRADES,
    itemsKey: "trade_history",
    pageSize,
    enabled,
    query: { instrument_type: instrumentType, asset },
    realtimeChannels,
  });
};
